import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Alert, AlertTitle, Button, Dialog, DialogContent, Divider, Stack, styled, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import PromptDialogHeader from "@entrilia/shared/components/dialog/PromptDialogHeader.tsx";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import LoaderBox from "@entrilia/shared/components/LoaderBox.tsx";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import type {
  GroupForSharing,
  ReportAuthorizationInfo,
  UserForSharing,
} from "../../../../../../../../api/types/svTypes.ts";
import { useBiApiClientProvider } from "../../../../contexts/BiApiClientProviderContext.ts";
import { useSharingReportContext } from "./contexts/SharingReportContext.tsx";
import { SharingReportContextProvider } from "./contexts/SharingReportContextProvider.tsx";
import CopyLinkButton from "./CopyLinkButton.tsx";
import EveryoneSharing from "./EveryoneSharing.tsx";
import FollowGroupSharingSwitch from "./FollowGroupSharingSwitch.tsx";
import type { CurrentUser, ReportCompany, SharedItemType, SharedReport } from "./ShareReport.types.ts";
import AuthorizedGroups from "./userGroups/AuthorizedGroups.tsx";
import AuthorizedUsers from "./userGroups/AuthorizedUsers.tsx";
import UserSelection from "./UserSelection.tsx";

interface Props {
  report: SharedReport;
  itemType?: SharedItemType;
  currentUser: CurrentUser;
  reportCompany: ReportCompany;
  onClose: () => void;
  onAuthorizationUpdated: (authorization: ReportAuthorizationInfo, inheritFromParent: boolean) => void;
}

export default function ShareReport({
  report,
  itemType,
  currentUser,
  reportCompany: company,
  onClose,
  onAuthorizationUpdated,
}: Props) {
  const { getReportSharing, updateReportSharing } = useBiApiClientProvider();

  const getReportSharingOptions = useCallback(
    () => getReportSharing(report.reportId),
    [report.reportId, getReportSharing]
  );
  const [data, error, { isFetching }] = useFetch(getReportSharingOptions);

  const owner = data?.authorization.users.find((u) => u.access === ReportAccessType.Owner);
  const noun = itemType ?? "report";

  return (
    <Dialog
      open={true}
      maxWidth="sm"
      fullWidth
      sx={{ ".MuiDialog-container": { alignItems: "start", pt: 20 } }}
      slotProps={{ paper: { sx: { minHeight: 250 } } }}
    >
      <PromptDialogHeader text={`Share ${noun} ${report.name}`} onClose={onClose} />
      <LoaderBox loading={isFetching} overlay>
        {error && <StyledAlert color="error">Access denied</StyledAlert>}
        {data && !error && (
          <SharingReportContextProvider
            currentUser={currentUser}
            report={report}
            owner={owner}
            company={company}
            defaultAuthorization={data.authorization}
            defaultInheritFromParent={data.inheritFromParent}
            onClose={onClose}
            onAuthorizationUpdated={onAuthorizationUpdated}
            updateReportSharing={updateReportSharing}
          >
            <ShareReportContent noun={noun} users={data.users} groups={data.groups} onClose={onClose} />
          </SharingReportContextProvider>
        )}
      </LoaderBox>
    </Dialog>
  );
}

interface ShareReportContentProps {
  noun: SharedItemType;
  users: UserForSharing[];
  groups: GroupForSharing[];
  onClose: () => void;
}
function ShareReportContent({ noun, users, groups, onClose }: ShareReportContentProps) {
  const { getViewReportUrl } = useBiApiClientProvider();
  const {
    currentUser,
    sharedForCompanyAccess,
    authorizedUsers,
    authorizedGroups,
    report,
    isAuthorizationChanged,
    isSaving,
    saveError,
    canInherit,
    inheritFromParent,
    setInheritFromParent,
    onUserSelected,
    onUserAccessChanged,
    onRemoveUser,
    onGroupSelected,
    onGroupAccessChanged,
    onRemoveGroup,
    onSave,
  } = useSharingReportContext();

  const isOwnerSet = useMemo(() => {
    return authorizedUsers.some((u) => u.access === ReportAccessType.Owner);
  }, [authorizedUsers]);

  const isReportPrivate = useMemo(() => {
    if (sharedForCompanyAccess !== ReportAccessType.NoAccess) {
      return false;
    }
    if (authorizedGroups.length > 0) {
      return false;
    }
    const usersCount = authorizedUsers.length || 0;
    if (usersCount === 1) {
      return authorizedUsers[0]?.userId === currentUser.id;
    }
    return !(usersCount > 1);
  }, [authorizedUsers, authorizedGroups, currentUser.id, sharedForCompanyAccess]);

  const reportUrl = `${window.location.origin}${getViewReportUrl(report.clientCode, report.reportId)}`;

  return (
    <>
      <DialogContent sx={{ pt: 0.5 }}>
        <Stack spacing={2} useFlexGap sx={{ width: "100%" }}>
          {saveError && <Alert severity="error">{saveError.message}</Alert>}
          {canInherit && (
            <FollowGroupSharingSwitch checked={inheritFromParent} disabled={isSaving} onChange={setInheritFromParent} />
          )}
          {inheritFromParent ? (
            <Alert severity="info">
              <AlertTitle>Access follows the group</AlertTitle>
              Manage who can open this {noun} from the group&apos;s sharing settings.
              <br />
              The owner always keeps access.
            </Alert>
          ) : (
            <>
              <EveryoneSharing disabled={isSaving} />
              <UserSelection
                disabled={isSaving}
                availableUsers={users}
                availableGroups={groups}
                authorizedUsers={authorizedUsers}
                authorizedGroups={authorizedGroups}
                currentUserId={currentUser.id}
                onUsersSelected={onUserSelected}
                onGroupsSelected={onGroupSelected}
              />
            </>
          )}
          {/* The backend always requires exactly one owner (kept even while inheriting), so enforce it regardless. */}
          {!isOwnerSet && (
            <StyledAlert severity="error">
              <Typography>The {noun} must have an owner. Please assign one.</Typography>
            </StyledAlert>
          )}
        </Stack>
      </DialogContent>
      {!inheritFromParent && (
        <>
          <Divider />
          <DialogContent sx={{ pt: 1 }}>
            {isReportPrivate && <PrivateReportMessage noun={noun} />}
            {!isReportPrivate && (
              <Typography variant="caption" color="textSecondary">
                More members with access
              </Typography>
            )}
            <Stack sx={{ pt: 1, gap: 2 }}>
              <AuthorizedGroups
                authorizedGroups={authorizedGroups}
                allGroups={groups}
                disabled={isSaving}
                onAccessChanged={onGroupAccessChanged}
                onRemoveGroup={onRemoveGroup}
              />
              <AuthorizedUsers
                currentUser={currentUser}
                currentUserAccess={report.authorization.access}
                authorizedUsers={authorizedUsers}
                allUsers={users}
                disabled={isSaving}
                onAccessChanged={onUserAccessChanged}
                onRemoveUser={onRemoveUser}
              />
            </Stack>
          </DialogContent>
        </>
      )}
      <Divider />
      <DialogButtons onCancel={onClose} startAction={<CopyLinkButton url={reportUrl} />}>
        <Button
          variant="contained"
          color="primary"
          disabled={!isAuthorizationChanged || !isOwnerSet}
          loading={isSaving}
          onClick={onSave}
        >
          Apply
        </Button>
      </DialogButtons>
    </>
  );
}

function PrivateReportMessage({ noun }: { noun: SharedItemType }) {
  return (
    <Stack direction={"row"} spacing={0.5} sx={{ alignItems: "center" }}>
      <LockOutlinedIcon sx={{ fontSize: "13px" }} color="secondary" />
      <Typography variant="body2" color="secondary">
        Only you have access to this {noun}
      </Typography>
    </Stack>
  );
}

const StyledAlert = styled(Alert)(({ theme }) => ({
  borderColor: theme.palette.error.main,
  borderWidth: 1,
  borderStyle: "solid",
  alignItems: "center",
}));
